import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  FormControl,
  FormLabel,
  TextField,
  Select,
  MenuItem,
  FormControlLabel,
  RadioGroup,
  Radio,
  Checkbox,
  CircularProgress,
  Alert,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AssignmentLateIcon from "@mui/icons-material/AssignmentLate";
import { postData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";

const FONT = "'Inter', 'Roboto', sans-serif";

const REASON_OPTIONS = ["Sick", "Personal", "Family Emergency", "Other"];

const FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: "#D5D7DA", borderRadius: "10px" },
    "&:hover fieldset": { borderColor: "#4A7BA8" },
    "&.Mui-focused fieldset": { borderColor: "#244C77" },
  },
};

const labelStyle = { color: "#6B7280", fontSize: "13px", mb: 0.5 };

function FieldLabel({ children, required }) {
  return (
    <FormLabel sx={labelStyle}>
      {children}
      {required && <span style={{ color: "#f44336", marginLeft: 2 }}>*</span>}
    </FormLabel>
  );
}

const EMPTY_FORM = {
  fromDate: "",
  toDate: "",
  reason: "",
  description: "",
  emergencyContactAvailable: "",
  substituteArranged: false,
};

export default function MandateLeaveForm() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  // Auto-compute total days for display
  const totalDays = (() => {
    if (!form.fromDate || !form.toDate) return null;
    const from = new Date(form.fromDate);
    const to = new Date(form.toDate);
    if (to < from) return null;
    return Math.round((to - from) / (1000 * 60 * 60 * 24)) + 1;
  })();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.fromDate) e.fromDate = "From Date is required";
    if (!form.toDate) e.toDate = "To Date is required";
    if (form.fromDate && form.toDate && new Date(form.fromDate) > new Date(form.toDate)) {
      e.toDate = "To Date must be on or after From Date";
    }
    if (!form.reason) e.reason = "Reason is required";
    if (form.reason === "Other" && !form.description.trim()) {
      e.description = "Description is required for 'Other' reason";
    }
    if (form.reason === "Other" && form.description.trim().length > 100) {
      e.description = "Description must be 100 characters or fewer";
    }
    if (!form.emergencyContactAvailable) {
      e.emergencyContactAvailable = "Please select Yes or No";
    }
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSubmitting(true);
    try {
      await postData("staff/mandate-leave", {
        fromDate: form.fromDate,
        toDate: form.toDate,
        reason: form.reason,
        description: form.description,
        emergencyContactAvailable: form.emergencyContactAvailable === "yes",
        substituteArranged: form.substituteArranged,
        totalDays,
        staffName: sessionStorage.getItem("userFullName") || "",
        staffEmail: sessionStorage.getItem("userEmail") || "",
      });
      setSubmitted(true);
      setToast({ open: true, severity: "success", message: "Mandatory leave form submitted successfully." });
    } catch {
      setToast({ open: true, severity: "error", message: "Submission failed. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setForm(EMPTY_FORM);
    setErrors({});
    setSubmitted(false);
  };

  return (
    <Box
      sx={{
        padding: "1.5rem",
        backgroundColor: "#F8F9FA",
        minHeight: "100vh",
        fontFamily: FONT,
      }}
    >
      <Toast
        open={toast.open}
        handleClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity}
        message={toast.message}
      />

      <Typography sx={{ color: "rgba(0,0,0,0.45)", fontWeight: "bold", fontSize: "14px", mb: 0.5 }}>
        Pages / Mandatory Leave Form
      </Typography>

      <Paper
        elevation={0}
        sx={{
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 2px 14px rgba(80,56,237,0.10)",
          maxWidth: 680,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #244C77 0%, #4A7BA8 100%)",
            px: 3,
            py: 2.5,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <AssignmentLateIcon sx={{ color: "#fff", fontSize: "24px" }} />
          <Box>
            <Typography sx={{ color: "#fff", fontWeight: "bold", fontSize: "17px", fontFamily: FONT }}>
              Mandatory Leave Form
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.75)", fontSize: "12px" }}>
              Required for absences of 2 or more consecutive days
            </Typography>
          </Box>
        </Box>

        <Box sx={{ p: 3 }}>
          {/* Success state */}
          {submitted ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <CheckCircleIcon sx={{ fontSize: 56, color: "#4caf50", mb: 2 }} />
              <Typography
                sx={{
                  fontWeight: "bold",
                  fontSize: "18px",
                  color: "#1a1a2e",
                  mb: 1,
                  fontFamily: FONT,
                }}
              >
                Form Submitted Successfully
              </Typography>
              <Typography sx={{ fontSize: "14px", color: "#6B7280", mb: 3 }}>
                Mandatory leave form submitted. Admin has been notified.
              </Typography>
              <Alert
                severity="success"
                sx={{ textAlign: "left", borderRadius: "10px", mb: 3 }}
              >
                <strong>Leave Details:</strong>
                <br />
                From: {new Date(form.fromDate).toLocaleDateString("en-US", { dateStyle: "long" })}
                <br />
                To: {new Date(form.toDate).toLocaleDateString("en-US", { dateStyle: "long" })}
                {totalDays && (
                  <>
                    <br />
                    Duration: {totalDays} day{totalDays !== 1 ? "s" : ""}
                  </>
                )}
                <br />
                Reason: {form.reason}
              </Alert>
              <Button
                onClick={handleReset}
                variant="outlined"
                sx={{
                  borderColor: "#244C77",
                  color: "#244C77",
                  textTransform: "none",
                  borderRadius: "10px",
                  fontWeight: 600,
                }}
              >
                Submit Another Request
              </Button>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Grid container spacing={2.5}>
                {/* From Date */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <FieldLabel required>From Date</FieldLabel>
                    <TextField
                      size="small"
                      name="fromDate"
                      type="date"
                      value={form.fromDate}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.fromDate}
                      helperText={errors.fromDate}
                      sx={FIELD_SX}
                    />
                  </FormControl>
                </Grid>

                {/* To Date */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <FieldLabel required>To Date</FieldLabel>
                    <TextField
                      size="small"
                      name="toDate"
                      type="date"
                      value={form.toDate}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.toDate}
                      helperText={errors.toDate}
                      sx={FIELD_SX}
                    />
                  </FormControl>
                </Grid>

                {/* Total days indicator */}
                {totalDays !== null && (
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        backgroundColor: "#EEF2FF",
                        border: "1px solid #C7D2FE",
                        borderRadius: "8px",
                        px: 2,
                        py: 1,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Typography sx={{ fontSize: "13px", color: "#244C77", fontWeight: 600 }}>
                        Total Duration: {totalDays} day{totalDays !== 1 ? "s" : ""}
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {/* Reason */}
                <Grid item xs={12}>
                  <FormControl fullWidth error={!!errors.reason}>
                    <FieldLabel required>Reason</FieldLabel>
                    <Select
                      size="small"
                      name="reason"
                      value={form.reason}
                      onChange={handleChange}
                      sx={{ borderRadius: "10px", fontSize: "13px" }}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        <span style={{ color: "#9CA3AF" }}>Select a reason</span>
                      </MenuItem>
                      {REASON_OPTIONS.map((r) => (
                        <MenuItem key={r} value={r}>
                          {r}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.reason && (
                      <Typography sx={{ fontSize: "11px", color: "#f44336", mt: 0.5 }}>
                        {errors.reason}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                {/* Description — only shown / required for "Other" */}
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <FieldLabel required={form.reason === "Other"}>
                      Description{form.reason !== "Other" ? " (optional)" : ""}
                    </FieldLabel>
                    <TextField
                      size="small"
                      name="description"
                      multiline
                      rows={3}
                      value={form.description}
                      onChange={handleChange}
                      placeholder={
                        form.reason === "Other"
                          ? "Describe the reason (max 100 characters)…"
                          : "Additional notes (optional)…"
                      }
                      inputProps={{ maxLength: form.reason === "Other" ? 100 : undefined }}
                      error={!!errors.description}
                      helperText={
                        errors.description ||
                        (form.reason === "Other"
                          ? `${form.description.length}/100 characters`
                          : "")
                      }
                      sx={FIELD_SX}
                    />
                  </FormControl>
                </Grid>

                {/* Emergency Contact Available */}
                <Grid item xs={12} sm={6}>
                  <FormControl error={!!errors.emergencyContactAvailable}>
                    <FieldLabel required>Emergency Contact Available?</FieldLabel>
                    <RadioGroup
                      name="emergencyContactAvailable"
                      value={form.emergencyContactAvailable}
                      onChange={handleChange}
                      row
                    >
                      <FormControlLabel
                        value="yes"
                        control={<Radio size="small" sx={{ color: "#244C77", "&.Mui-checked": { color: "#244C77" } }} />}
                        label={<Typography sx={{ fontSize: "13px" }}>Yes</Typography>}
                      />
                      <FormControlLabel
                        value="no"
                        control={<Radio size="small" sx={{ color: "#244C77", "&.Mui-checked": { color: "#244C77" } }} />}
                        label={<Typography sx={{ fontSize: "13px" }}>No</Typography>}
                      />
                    </RadioGroup>
                    {errors.emergencyContactAvailable && (
                      <Typography sx={{ fontSize: "11px", color: "#f44336" }}>
                        {errors.emergencyContactAvailable}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                {/* Substitute Arranged */}
                <Grid item xs={12} sm={6}>
                  <FormControl>
                    <FieldLabel>Substitute Arranged?</FieldLabel>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="substituteArranged"
                          checked={form.substituteArranged}
                          onChange={handleChange}
                          sx={{ color: "#244C77", "&.Mui-checked": { color: "#244C77" } }}
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: "13px", color: "#374151" }}>
                          Yes, a substitute has been arranged
                        </Typography>
                      }
                    />
                  </FormControl>
                </Grid>
              </Grid>

              {/* Action buttons */}
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 4 }}>
                <Button
                  type="button"
                  onClick={handleReset}
                  sx={{
                    color: "#6B7280",
                    textTransform: "none",
                    border: "1px solid #D5D7DA",
                    borderRadius: "10px",
                    px: 3,
                    fontWeight: 600,
                  }}
                >
                  Reset
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={submitting}
                  startIcon={
                    submitting ? (
                      <CircularProgress size={16} sx={{ color: "#fff" }} />
                    ) : null
                  }
                  sx={{
                    background: "linear-gradient(135deg, #244C77 0%, #4A7BA8 100%)",
                    color: "#fff",
                    textTransform: "none",
                    borderRadius: "10px",
                    px: 4,
                    fontWeight: 600,
                    "&:hover": {
                      background: "linear-gradient(135deg, #244C77 0%, #4A7BA8 100%)",
                    },
                    "&.Mui-disabled": { background: "#E5E7EB", color: "#9CA3AF" },
                  }}
                >
                  {submitting ? "Submitting…" : "Submit Form"}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
